import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditDialog } from '../../../EditDialog';
import { EditHrEmployeeSelectField } from '../../../EditHrEmployeeSelectField';
import type { ContactOtherFieldsData } from '../queries/contactOthersQuery';
import { GET_CONTACT_OTHERS } from '../queries/contactOthersQuery';

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        ny2QaCheckNeeded
        ny2QaCheckedBy {
          id
          name
        }
        owningUser {
          id
          name
        }
        test1
        test2
      }
    }
  }
`;

const SYSTEM_QA_FIELDS: Record<
  string,
  { apiField: string; isBoolean?: boolean }
> = {
  ny2QaCheckedById: { apiField: 'ny2QaCheckedBy' },
  ny2QaCheckNeeded: { apiField: 'ny2QaCheckNeeded', isBoolean: true },
  owningUserId: { apiField: 'owningUser' },
  test1: { apiField: 'test1', isBoolean: true },
  test2: { apiField: 'test2', isBoolean: true }
};

export interface EditSystemQaDialogProps {
  contactId: string;
  initialValue: ContactOtherFieldsData;
  isOpen: boolean;
  onChange: (data: ContactOtherFieldsData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactOtherFieldsData;
}

export function EditSystemQaDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditSystemQaDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [{ query: GET_CONTACT_OTHERS, variables: { contactId } }]
  });

  const changes = getChangedValues(value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof ContactOtherFieldsData>(
    field: K,
    fieldValue: ContactOtherFieldsData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: { id: contactId, values: changes }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save System / QA');
      } else {
        toast.success('System / QA saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving System / QA:', error);
      toast.error('Failed to save System / QA');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit System / QA"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditCheckboxField
          checked={value.ny2QaCheckNeeded}
          label="NY2 - QA Check Needed"
          onCheckedChange={v => {
            updateField('ny2QaCheckNeeded', v);
          }}
        />
        <EditHrEmployeeSelectField
          label="NY2 QA Checked By"
          onChange={(id, name) => {
            onChange({
              ...value,
              ny2QaCheckedBy: name,
              ny2QaCheckedById: id || null
            });
          }}
          value={
            value.ny2QaCheckedById
              ? { id: value.ny2QaCheckedById, name: value.ny2QaCheckedBy }
              : null
          }
        />
        <EditHrEmployeeSelectField
          label="Owning User"
          onChange={(id, name) => {
            onChange({
              ...value,
              owningUser: name,
              owningUserId: id || null
            });
          }}
          value={
            value.owningUserId
              ? { id: value.owningUserId, name: value.owningUser }
              : null
          }
        />
        <EditCheckboxField
          checked={value.test1}
          label="Test1"
          onCheckedChange={v => {
            updateField('test1', v);
          }}
        />
        <EditCheckboxField
          checked={value.test2}
          label="Test2"
          onCheckedChange={v => {
            updateField('test2', v);
          }}
        />
      </div>
    </EditDialog>
  );
}

function getChangedValues(
  current: ContactOtherFieldsData,
  initial: ContactOtherFieldsData
): Record<string, boolean | null | string> {
  const changes: Record<string, boolean | null | string> = {};

  for (const [uiField, config] of Object.entries(SYSTEM_QA_FIELDS)) {
    const currentVal = current[uiField as keyof ContactOtherFieldsData];
    const initialVal = initial[uiField as keyof ContactOtherFieldsData];

    if (currentVal !== initialVal) {
      if (config.isBoolean) {
        changes[config.apiField] = currentVal as boolean;
      } else {
        const strVal = currentVal as null | string;
        changes[config.apiField] = strVal === '' ? null : (strVal ?? null);
      }
    }
  }

  return changes;
}
